import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as SmsDailySearchFormInitialData,
  ValidData as SmsDailySearchFormValidData,
} from '@/components/searchForms/SmsDailySearchForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import useDownloadCsv from '@/composables/useDownloadCsv';
import { fetchGetSmsDaily, fetchPostSmsDaily } from '@/helpers/fetchers';
import * as locationQueryHelpers from '@/helpers/locationQuery';

type SMSItem = openapi.SMSDailyListItem;

export interface SmsDailyViewComposable {
  search: (formData: SmsDailySearchFormValidData) => void;
  initialData: ComputedRef<SmsDailySearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  isSendingMessages: Ref<boolean>;
  smsDailyData: Ref<openapi.SMSDailyContentOpenapi>;
  handleSendClick: (selectedIds: number[]) => Promise<void>;
  downloadCsvSmsDaily: () => Promise<void>;
  isDownloadingCSV: Ref<boolean>;
}

export default function useSmsDailyView(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
  queryDate: Readonly<Ref<Date | undefined>>,
  queryDivision: Readonly<Ref<string | undefined>>,
  queryDriver: Readonly<Ref<number | undefined>>,
): SmsDailyViewComposable {
  const router = useRouter();
  const route = useRoute();
  const { downloadCsv, isDownloadingCSV } = useDownloadCsv();

  const initialData = computed<SmsDailySearchFormInitialData>(() => {
    let date;
    if (queryDate.value) {
      date = format(queryDate.value, 'yyyy-MM-dd');
    }
    const driver = queryDriver.value;
    return {
      driver,
      division: queryDivision.value,
      date,
    };
  });

  const isFetchingData = ref(false);
  const isSendingMessages = ref(false);

  const smsDailyData = ref<openapi.SMSDailyContentOpenapi>({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });

  function search(formData: SmsDailySearchFormValidData): void {
    const query = locationQueryHelpers.buildSmsDailyLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: route.name || 'smsDailyView',
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    const page = queryCurPage.value;
    const pageSize = queryPageSize.value;

    const date = queryDate.value;
    const driver = queryDriver.value;
    const division = queryDivision.value;

    smsDailyData.value = {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };

    if (!date) return;
    isFetchingData.value = true;

    await fetchGetSmsDaily({
      date,
      driver,
      division,
      page,
      page_size: pageSize,
    })
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        smsDailyData.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  async function handleSendClick(selectedIds: number[]): Promise<void> {
    const date = queryDate.value;
    if (!selectedIds.length) return;
    if (!date) return;

    const selectedItems = smsDailyData.value.results.filter((item: SMSItem) =>
      selectedIds.includes(Number(item.job_id)),
    );

    const requestData: openapi.SMSSendRequestOpenapi = {
      items: selectedItems.map((item: SMSItem) => ({
        driver: item.driver?.code || 0,
        offsider: parseInt(item.offsider || '') || null,
        extra_number: item.extra_number,
        content: item.sms_message?.content || '',
      })),
    };

    isSendingMessages.value = true;

    try {
      await fetchPostSmsDaily(requestData);
      await fetchData(route.query);
    } finally {
      isSendingMessages.value = false;
    }
  }

  async function downloadCsvSmsDaily(): Promise<void> {
    const date = queryDate.value;
    if (!date) return;
    downloadCsv(smsDailyData.value.results, 'SMS Daily Report');
  }

  watch(
    route,
    async (newRoute) => {
      await fetchData(newRoute.query);
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    isSendingMessages,
    smsDailyData,
    handleSendClick,
    downloadCsvSmsDaily,
    isDownloadingCSV,
  };
}
